// @ts-nocheck
/**
 * API wrappers + SSE consumer for the conversational mapping agent.
 *
 * Endpoints mirror agent/router.py:
 *   POST   /agent/sessions
 *   POST   /agent/sessions/{id}/upload
 *   POST   /agent/sessions/{id}/files/{file_id}/sheet
 *   POST   /agent/sessions/{id}/chat  (SSE)
 *   GET    /agent/sessions/{id}        → { session_id, status, file_refs, view_state }
 *   GET    /agent/sessions
 *   DELETE /agent/sessions/{id}
 *
 * Resuming a completed session is a single GET against the server-side
 * view_state column; nothing is reconstructed from raw messages anymore.
 */

const BASE = import.meta.env.VITE_BACKEND_BASE_URL;
const API_KEY = import.meta.env.VITE_API_KEY;

const jsonHeaders = {
  'Content-Type': 'application/json',
  'x-api-key': API_KEY,
};

export async function createAgentSession() {
  const res = await fetch(`${BASE}/agent/sessions`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
  });
  if (!res.ok) throw new Error(`Create agent session failed: ${res.status}`);
  return res.json();
}

/**
 * Upload a single Excel file to an agent session.
 *
 * Response shape:
 *   { file_id, filename, size_bytes, sheets: string[] }
 *
 * `sheets` is the workbook's tab list — the caller shows a picker when
 * there's more than one and POSTs `selectAgentSheet` before kicking off
 * the chat stream.
 */
export async function uploadAgentFile(sessionId, file, statementType = 'pnl') {
  const form = new FormData();
  form.append('file', file);
  form.append('statement_type', statementType);
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/upload`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
    body: form,
  });
  if (!res.ok) throw new Error(`Upload to agent session failed: ${res.status}`);
  return res.json();
}

/**
 * Persist the user's sheet choice on the session's file_ref. The chat
 * endpoint refuses to start the agent until this has been called.
 */
export async function selectAgentSheet(sessionId, fileId, sheetName) {
  const res = await fetch(
    `${BASE}/agent/sessions/${sessionId}/files/${fileId}/sheet`,
    {
      method: 'POST',
      credentials: 'include',
      headers: jsonHeaders,
      body: JSON.stringify({ sheet_name: sheetName }),
    },
  );
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Select sheet failed: ${res.status} ${txt}`);
  }
  return res.json();
}

/**
 * Load a completed session.
 *
 * Response: `{ session_id, status, file_refs, view_state }`
 *
 * `view_state.chat_items` is the same shape the live SSE reducer
 * produces during a fresh run, so the caller can hand it straight to
 * setChatItems with no transformation.
 */
export async function getAgentSession(sessionId) {
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}`, {
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (!res.ok) throw new Error(`Load agent session failed: ${res.status}`);
  return res.json();
}

/**
 * Compute the P&L ratio time series for a completed session's mapping.
 *
 * Read-only and on-demand — the backend derives everything from the
 * persisted mapping (``GET /agent/sessions/{id}/ratios``), so this works
 * for terminated sessions too. Response shape mirrors
 * ``src/pl_ratios/schemas.py:RatiosResult``:
 *   { periods, subtotals, ratios, common_size, headline, reconciliation,
 *     insufficient_data, notes }
 *
 * Throws on non-2xx with the server's ``detail`` so the caller can surface
 * a meaningful message (e.g. a 404 when the run hasn't produced a mapping).
 */
export async function getAgentRatios(sessionId) {
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/ratios`, {
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = j?.detail || '';
    } catch {
      /* not JSON, leave detail empty */
    }
    throw new Error(
      `Load ratios failed (${res.status})${detail ? `: ${detail}` : ''}`,
    );
  }
  return res.json();
}

/**
 * Fetch the tie-out provenance ledger for a session. Read-only and computed
 * on demand from the immutable mapping + source file (cached server-side).
 *
 * For every standardized line it proves the exported ``='Source Data'!cell``
 * reference resolves to the value we mapped — and flags any that doesn't —
 * joined to the line's audit trail (classification reasoning, any correction).
 *
 * Returns ``null`` on 404 (no completed mapping yet) so the caller shows an
 * empty state instead of an error. The endpoint degrades to a
 * ``referencing_available: false`` ledger (200) when cell-referencing can't
 * run, so callers never need to special-case a missing source file here.
 *
 * Response shape (per ``agent/router.py:_assemble_provenance``):
 *   { session_id, source: {filename, sheet_name, is_csv},
 *     reconciliation, tolerance, referencing_available,
 *     counts: {total, tie, flag, source_unavailable, not_referenced},
 *     notes: string[],
 *     lines: [{ row_index, account_name, category, corrected, tie_status,
 *       source_cell, is_formula, source_formula, claimed_value,
 *       resolved_value, delta, classification_reasoning,
 *       classification_confidence, subcategory, correction, per_period }] }
 */
export async function getProvenance(sessionId) {
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/provenance`, {
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = j?.detail || '';
    } catch {
      /* not JSON */
    }
    throw new Error(
      `Load tie-out failed (${res.status})${detail ? `: ${detail}` : ''}`,
    );
  }
  return res.json();
}

/**
 * Fetch the stored P&L insight (PE-analyst "first look") for a session.
 * Read-only — the server never generates here. Returns ``null`` when none has
 * been produced yet (404) so the caller shows a Generate affordance instead of
 * treating it as an error; other non-2xx throw with the server's ``detail``.
 * Response shape mirrors ``agent/insights/schemas.py:InsightResult``:
 *   { verdict, insights, blind_spots, schema_version, generated_at, scale }
 */
export async function getAgentInsights(sessionId) {
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/insights`, {
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = j?.detail || '';
    } catch {
      /* not JSON */
    }
    throw new Error(
      `Load insights failed (${res.status})${detail ? `: ${detail}` : ''}`,
    );
  }
  return res.json();
}

/**
 * Generate (or force-regenerate) the P&L insight for a completed session. A
 * single-shot analyst pass over the computed ratios + P&L — runs only on
 * demand and can take ~20-60s. Returns the InsightResult payload.
 *
 * Throws on non-2xx with the server's ``detail`` (e.g. a 409 when the run is
 * still active, or a 502 if generation failed) so the caller can surface it.
 */
export async function generateAgentInsights(sessionId, { force = false } = {}) {
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/insights`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({ force }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = j?.detail || '';
    } catch {
      /* not JSON */
    }
    const err = new Error(
      `Generate insights failed (${res.status})${detail ? `: ${detail}` : ''}`,
    );
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Fetch the stored adjusted-EBITDA bridge for a session. Read-only — the server
 * never generates here. Returns ``null`` on 404 (none generated yet) so the
 * caller shows a Generate affordance instead of an error. Response mirrors
 * ``agent/ebitda_bridge/schemas.py:EBITDABridgeResult``:
 *   { periods, add_backs, bridge, reconciliation, generated_at, notes }
 */
export async function getAgentEbitdaBridge(sessionId) {
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/ebitda-bridge`, {
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = j?.detail || '';
    } catch {
      /* not JSON */
    }
    throw new Error(
      `Load EBITDA bridge failed (${res.status})${detail ? `: ${detail}` : ''}`,
    );
  }
  return res.json();
}

/**
 * Generate (or force-regenerate) the adjusted-EBITDA bridge for a completed
 * session. One Opus pass classifying variance-flagged add-back candidates from
 * the P&L alone (~20-60s). Returns the EBITDABridgeResult payload.
 *
 * Throws on non-2xx with the server's ``detail`` (e.g. 409 while the run is
 * active, 502 on generation failure).
 */
export async function generateAgentEbitdaBridge(sessionId, { force = false } = {}) {
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/ebitda-bridge`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({ force }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = j?.detail || '';
    } catch {
      /* not JSON */
    }
    const err = new Error(
      `Generate EBITDA bridge failed (${res.status})${detail ? `: ${detail}` : ''}`,
    );
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Set which add-backs are approved (only these count toward adjusted EBITDA).
 * The server recomputes the waterfall from the stored reported EBITDA — no LLM —
 * and returns the updated bridge. ``approvedIds`` is the full approved set.
 */
export async function updateAgentEbitdaBridgeApprovals(sessionId, approvedIds) {
  const res = await fetch(
    `${BASE}/agent/sessions/${sessionId}/ebitda-bridge/approvals`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: jsonHeaders,
      body: JSON.stringify({ approved_ids: approvedIds }),
    },
  );
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = j?.detail || '';
    } catch {
      /* not JSON */
    }
    throw new Error(
      `Save approvals failed (${res.status})${detail ? `: ${detail}` : ''}`,
    );
  }
  return res.json();
}

export async function listAgentSessions() {
  const res = await fetch(`${BASE}/agent/sessions`, {
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (!res.ok) throw new Error(`List agent sessions failed: ${res.status}`);
  return res.json();
}

export async function deleteAgentSession(sessionId) {
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (!res.ok) throw new Error(`Delete agent session failed: ${res.status}`);
  return res.json();
}

/**
 * Persist user category edits on the final mapping. Sends a
 * ``{row_index: new_category}`` map to ``PATCH /agent/sessions/{id}/mapping``.
 * The server applies the overrides, re-runs net-income reconciliation
 * against the persisted ``value_by_period`` (no source-file roundtrip),
 * and returns the refreshed mapping (accounts + reconciliation +
 * period_breakdowns) so the caller can swap it into ``chat_items`` in
 * place.
 *
 * Throws on non-2xx with the server's ``detail`` field if present, so
 * callers can surface a meaningful error (e.g. a 409 when the run is
 * still active).
 */
export async function patchAgentMapping(sessionId, overrides) {
  // ``overrides`` arrives keyed by integer row_index; JSON object keys
  // are always strings, so coerce explicitly to keep the wire shape
  // predictable across browsers.
  const body = {
    overrides: Object.fromEntries(
      Object.entries(overrides).map(([k, v]) => [String(k), v]),
    ),
  };
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/mapping`, {
    method: 'PATCH',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = j?.detail || '';
    } catch {
      /* not JSON, leave detail empty */
    }
    throw new Error(
      `Patch agent mapping failed (${res.status})${detail ? `: ${detail}` : ''}`,
    );
  }
  const payload = await res.json();
  return payload.mapping;
}

/**
 * Fire-and-forget: kick off an agent run in the background.
 *
 * The HTTP request returns as soon as the server has spawned the task
 * — the run itself survives client disconnect and refresh. Call
 * `watchAgentSession` to attach to the event stream.
 *
 * On 200, the response carries the watch URL. On 409 (another tab is
 * already running this session), the response body's `detail` carries
 * the offset to attach to so both tabs see the same events. Both cases
 * resolve to a single shape:
 *
 *   { ok: true,  stream_url, conflict: false, last_event_id: null }
 *   { ok: false, stream_url, conflict: true,  last_event_id }
 */
export async function sendAgentMessage(sessionId, message, mode = 'pnl') {
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/chat`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({ message, mode }),
  });
  if (res.status === 409) {
    // Another worker is already running the session. The server tells
    // us where to attach so duplicate tabs converge on a single stream.
    const body = await res.json().catch(() => ({}));
    const detail = body?.detail || {};
    return {
      ok: false,
      conflict: true,
      stream_url: detail.stream_url,
      last_event_id: detail.last_event_id || '0',
    };
  }
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Agent chat failed: ${res.status} ${txt}`);
  }
  const body = await res.json();
  return {
    ok: true,
    conflict: false,
    stream_url: body.stream_url,
    last_event_id: null,
  };
}

/**
 * Kick off a unified statements run (P&L child then BS child, chained
 * server-side). `pair` is `{ pnl: { file_id, sheet_name }, bs: { file_id,
 * sheet_name } }` — both slots may reference the same workbook.
 *
 * Resolves `{ ok, conflict, phases }` where `phases` is
 * `[{ statement_type, session_id, stream_url }]` in chain order. A 409
 * (chain already running) resolves with `conflict: true` and the same
 * phases so a second tab attaches instead of double-running.
 */
export async function startUnifiedRun(sessionId, pair) {
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/unified-run`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(pair),
  });
  if (res.status === 409) {
    const body = await res.json().catch(() => ({}));
    const detail = body?.detail || {};
    return { ok: false, conflict: true, phases: detail.phases || [] };
  }
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail =
        typeof j?.detail === 'string'
          ? j.detail
          : j?.detail?.message || JSON.stringify(j?.detail || '');
    } catch {
      /* body not json */
    }
    throw new Error(detail || `Unified run failed: ${res.status}`);
  }
  const body = await res.json();
  return { ok: true, conflict: false, phases: body.phases || [] };
}

/**
 * Load a unified parent session's snapshot: parent status + the pairing +
 * a full per-child snapshot (same shape as `getAgentSession`) under
 * `children.pnl` / `children.bs`.
 */
export async function getUnifiedSession(sessionId) {
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/unified`, {
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (!res.ok) throw new Error(`Load unified session failed: ${res.status}`);
  return res.json();
}

/**
 * Upload one workbook to a Databook session. Same endpoint and response
 * (`{ file_id, filename, size_bytes, sheets }`) as `uploadAgentFile`, but
 * tagged `session_kind=databook` and carrying no statement type: a Databook
 * session takes many workbooks and the backend works out which sheets feed
 * which statement.
 *
 * Callers upload ONE FILE AT A TIME (await each call before the next);
 * parallel uploads to the same session race server-side. `signal` aborts
 * the request.
 */
export async function uploadDatabookFile(sessionId, file, signal) {
  const form = new FormData();
  form.append('file', file);
  form.append('session_kind', 'databook');
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/upload`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
    body: form,
    signal,
  });
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = typeof j?.detail === 'string' ? j.detail : '';
    } catch {
      /* body not json */
    }
    throw new Error(detail || `Upload failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Kick off a Databook run over the uploaded workbooks named in `fileIds`
 * (a consolidation step, then the P&L child run, then the BS child run,
 * chained server-side).
 *
 * Resolves `{ ok, conflict, phases }` exactly like `startUnifiedRun`:
 * `phases` is `[{ statement_type, session_id, stream_url }]` in chain order,
 * and a 409 (run already in flight) resolves with `conflict: true` and the
 * same phases so the caller attaches instead of failing.
 */
export async function startDatabookRun(sessionId, fileIds) {
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/databook-run`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({ file_ids: fileIds }),
  });
  if (res.status === 409) {
    const body = await res.json().catch(() => ({}));
    const detail = body?.detail || {};
    return { ok: false, conflict: true, phases: detail.phases || [] };
  }
  if (!res.ok) {
    let detail = '';
    try {
      const d = (await res.json())?.detail;
      detail =
        typeof d === 'string'
          ? d
          : d?.message || d?.error || (d ? JSON.stringify(d) : '');
    } catch {
      /* body not json */
    }
    throw new Error(detail || `Databook run failed: ${res.status}`);
  }
  const body = await res.json();
  return { ok: true, conflict: false, phases: body.phases || [] };
}

/**
 * Load a Databook parent session's snapshot:
 *   { session_id, status, session_kind: 'databook', file_refs,
 *     inputs: [{ file_id, filename, size_bytes, sheets }],
 *     pair: null | { pnl: { child_session_id }, bs: { child_session_id } },
 *     children: { pnl, bs },   // per-child snapshot, as in `getUnifiedSession`
 *     taxonomy: { id, pnl: [...], bs: [...] } }
 *
 * Unlike the unified snapshot this also answers BEFORE the first run (`pair`
 * and both children are null then), so a refresh during setup can restore
 * the uploaded list from `inputs`.
 */
export async function getDatabookSession(sessionId) {
  const res = await fetch(`${BASE}/agent/sessions/${sessionId}/databook`, {
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (!res.ok) throw new Error(`Load databook session failed: ${res.status}`);
  return res.json();
}

/**
 * Working-capital / CCC analysis for a unified parent session
 * (``GET /agent/sessions/{id}/working-capital``). Deterministic and cached
 * server-side; 409 while the chain is still running, 404 until both
 * children have completed mappings. Response mirrors
 * ``src/wc_ccc/schemas.py:WcResult``.
 */
export async function getWorkingCapital(sessionId) {
  const res = await fetch(
    `${BASE}/agent/sessions/${sessionId}/working-capital`,
    {
      credentials: 'include',
      headers: { 'x-api-key': API_KEY },
    },
  );
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = typeof j?.detail === 'string' ? j.detail : '';
    } catch {
      /* body not json */
    }
    throw new Error(detail || `Working capital failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Subscribe to a session's SSE event stream.
 *
 * `onEvent({ type, data }, eventId)` fires for every SSE frame except
 * the wire-level `_terminal_marker` (which signals the run is durably
 * persisted and we should close cleanly — filtered here before
 * reaching the caller's reducer). Event types match agent/runtime.py:
 *   - text_delta:       { text }
 *   - tool_start:       { tool_name, tool_use_id, input }
 *   - tool_result:      { tool_name, tool_use_id, is_error, result }
 *   - mapping_complete: { accounts, periods, reconciliation, ... }
 *   - done:             {}
 *   - error:            { message }
 *
 * `eventId` is the Redis Stream entry id (`<ms-ts>-<seq>`). Stash it in
 * a ref so refresh / reconnect can resume from `?from=<eventId>` and
 * not re-apply events the reducer has already seen.
 *
 * Resolves on terminal marker or aborted signal. Caller is responsible
 * for any retry-on-disconnect logic.
 */
export async function watchAgentSession(
  sessionId,
  fromEventId,
  onEvent,
  signal,
) {
  // Auto-reconnect loop. The SSE response can be cut by proxies, idle
  // timeouts, NAT, sleeping laptops, etc. — anything between the dyno
  // and the browser that decides "this connection has been quiet too
  // long" will close it. Without reconnect, the UI freezes on the
  // snapshot until the user manually remounts (the bug the user just
  // reported). We resume from the last event id we saw so events the
  // dropped connection missed get replayed on the next attach.
  let currentOffset = fromEventId || '0';
  let totalEvents = 0;
  let reconnects = 0;
  const MAX_RECONNECTS = 30;
  const BASE_BACKOFF_MS = 400;

  while (true) {
    if (signal?.aborted) return;

    const offset = encodeURIComponent(currentOffset);
    // eslint-disable-next-line no-console
    console.log(
      `[agent watch] attaching ${sessionId} from=${offset} ` +
      `(reconnect=${reconnects})`,
    );

    let res;
    try {
      res = await fetch(
        `${BASE}/agent/sessions/${sessionId}/stream?from=${offset}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'x-api-key': API_KEY, Accept: 'text/event-stream' },
          signal,
        },
      );
    } catch (err) {
      if (signal?.aborted || err.name === 'AbortError') return;
      if (reconnects >= MAX_RECONNECTS) throw err;
      const delay = Math.min(BASE_BACKOFF_MS * 2 ** reconnects, 30000);
      // eslint-disable-next-line no-console
      console.warn(
        `[agent watch] fetch failed for ${sessionId}, retrying in ${delay}ms`,
        err,
      );
      await sleep(delay);
      reconnects += 1;
      continue;
    }

    if (!res.ok) {
      // 4xx is a permanent error (not-found / not-owned) — don't loop.
      const txt = await res.text().catch(() => '');
      throw new Error(`Watch session failed: ${res.status} ${txt}`);
    }
    if (!res.body) throw new Error('Watch session: no response body');

    // eslint-disable-next-line no-console
    console.log(`[agent watch] connected ${sessionId}`);
    reconnects = 0; // a successful connection resets the backoff

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let terminalSeen = false;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames separated by blank lines. Each frame carries an
        // `id:` (Redis stream entry id, used for the reconnect cursor)
        // and a `data:` (the JSON payload). The reducer doesn't need
        // to see the id; we surface it so the caller can track
        // lastEventId for reattach.
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';

        for (const frame of frames) {
          let eventId = null;
          let dataLine = null;
          for (const line of frame.split('\n')) {
            if (line.startsWith('id: ')) {
              eventId = line.slice(4).trim();
            } else if (line.startsWith('data: ')) {
              dataLine = line.slice(6).trim();
            }
          }
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine);
            // Wire-level marker — close cleanly, don't reconnect.
            if (parsed?.type === '_terminal_marker') {
              // eslint-disable-next-line no-console
              console.log(`[agent watch] terminal marker ${sessionId}`);
              terminalSeen = true;
              return;
            }
            totalEvents += 1;
            if (eventId) currentOffset = eventId;
            onEvent(parsed, eventId);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('agent SSE parse failed', err, dataLine);
          }
        }
      }
    } catch (err) {
      if (signal?.aborted || err.name === 'AbortError') return;
      // eslint-disable-next-line no-console
      console.warn(`[agent watch] read error for ${sessionId}, will reconnect`, err);
    }

    // Reached end of body without a terminal marker — the stream was
    // closed by the server/proxy mid-run. Reconnect from the last id
    // we saw so we don't miss the events that flowed in the meantime.
    if (terminalSeen) return;
    if (signal?.aborted) return;
    if (reconnects >= MAX_RECONNECTS) {
      throw new Error(
        `Watch session: stream closed without terminal after ` +
        `${MAX_RECONNECTS} reconnect attempts (last offset ${currentOffset})`,
      );
    }
    const delay = Math.min(BASE_BACKOFF_MS * 2 ** reconnects, 30000);
    // eslint-disable-next-line no-console
    console.log(
      `[agent watch] stream closed without terminal for ${sessionId}, ` +
      `reconnecting from ${currentOffset} in ${delay}ms ` +
      `(received ${totalEvents} events so far)`,
    );
    await sleep(delay);
    reconnects += 1;
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
